import React, { useState } from 'react';
import InteractiveReportViewer from '../components/sections/InteractiveReportViewer';
import InteractiveReport from '../components/reports/InteractiveReport';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/Button';

const ReportPage: React.FC = () => {
  const [activeDemo, setActiveDemo] = useState<'pdf' | 'web'>('pdf');
  const [selectedPdfReportType] = useState('uti_ud_112');

  return (
    <div className="min-h-screen">
      {/* Hero Section with Gradient Background */}
      <div className="bg-gradient-to-br from-primary/90 to-secondary/90 text-text-light py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-primary">
              Clinical Microbiology Reports
            </h1>
            <p className="text-lg md:text-xl max-w-3xl mx-auto opacity-90 text-primary">
              Explore our comprehensive PCR panel reports with both PDF and interactive web formats.
            </p>
            
            {/* Demo Type Selector */}
            <div className="mt-8 flex justify-center">
              <div className="bg-background/20 backdrop-blur-sm rounded-lg p-2 inline-flex">
                <button
                  onClick={() => setActiveDemo('pdf')}
                  className={`px-6 py-2 rounded-md font-medium transition-all ${
                    activeDemo === 'pdf'
                      ? 'bg-primary text-white shadow-md'
                      : 'text-primary hover:bg-primary/10'
                  }`}
                >
                  PDF Report Demo
                </button>
                <button
                  onClick={() => setActiveDemo('web')}
                  className={`px-6 py-2 rounded-md font-medium transition-all ${
                    activeDemo === 'web'
                      ? 'bg-primary text-white shadow-md'
                      : 'text-primary hover:bg-primary/10'
                  }`}
                >
                  Interactive Web Demo
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 -mt-6">
        {/* PDF Report Demo */}
        {activeDemo === 'pdf' && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="bg-background-alt rounded-xl shadow-2xl p-8 mb-16 border border-border"
          >
            <div className="flex flex-row justify-between items-center w-full mb-4">
                <h2 className="text-2xl font-semibold text-primary">
                  Interactive PDF Report Demo
                </h2>
                <button 
                  onClick={() => setActiveDemo('web')}
                  className="btn btn-secondary bg-primary text-white rounded-lg px-4 py-2"
                >
                  View Web Report Demo
                </button>
              </div>
            <div className="mb-8 max-w-3xl mx-auto">
              
              <p className="text-text text-center mb-6">
                Click on the dots to learn more about each section of the report. Discover how our system identifies pathogens, 
                analyzes resistance patterns, and provides treatment recommendations.
              </p>
            </div>
            
            <InteractiveReportViewer selectedReportType={selectedPdfReportType} />
          </motion.div>
        )}

        {/* Interactive Web Report Demo */}
        {activeDemo === 'web' && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mb-16"
          >
            <InteractiveReport initialTab="summary" />
          </motion.div>
        )}

        <h2 className="text-3xl font-bold text-primary mb-8 text-center">Key Features</h2>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
        >
          <div className="bg-gradient-to-br from-background to-background-alt p-8 rounded-lg shadow-lg border-t-4 border-primary">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6 mx-auto">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-primary mb-4 text-center">
              Comprehensive Analysis
            </h3>
            <p className="text-text text-center">
              Our reports provide detailed analysis of microbial identification, antimicrobial susceptibility patterns, and personalized clinical interpretation.
            </p>
          </div>

          <div className="bg-gradient-to-br from-background to-background-alt p-8 rounded-lg shadow-lg border-t-4 border-accent">
            <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mb-6 mx-auto">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-accent mb-4 text-center">
              Clear Presentation
            </h3>
            <p className="text-text text-center">
              Information is organized in a clear, easy-to-read format with interactive elements for better understanding of complex clinical data.
            </p>
          </div>

          <div className="bg-gradient-to-br from-background to-background-alt p-8 rounded-lg shadow-lg border-t-4 border-secondary">
            <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mb-6 mx-auto">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-secondary mb-4 text-center">
              Actionable Insights
            </h3>
            <p className="text-text text-center">
              Each report includes expert interpretation and evidence-based treatment recommendations to guide clinical decision-making.
            </p>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="bg-primary/5 rounded-xl p-8 border border-primary/20 text-center"
        >
          <h3 className="text-2xl font-semibold text-primary mb-4">Ready to Experience Better Clinical Reporting?</h3>
          <p className="text-lg text-text mb-6 max-w-3xl mx-auto">
            See how our innovative reporting system can transform your clinical workflow and improve patient outcomes.
          </p>
          <Button
            href="/contact"
            variant="primary"
            size="lg"
            className="shadow-md"
          >
            Request a Demo
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default ReportPage; 